import { useState, useEffect } from "react";
import { Trash2, Upload, Phone, FileText, MapPin, Mail, Briefcase, Paperclip, MessageSquare, UserPlus, Pencil } from "lucide-react";
import { candidateAPI } from "../api/client";
import QuickActionBar from "./shared/QuickActionBar"; // Task 33: Unified Quick-Action Layer
import axiosInstance from "../config/axiosConfig";

function CandidateCard({ candidate, onDelete, onEdit, onResumeUpload, onCallLog, onWhatsApp, onAddFollowUp, isSelected, onToggleSelect, callLogCount = 0, onStatusChange, currentUser }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [downloadingResume, setDownloadingResume] = useState(false);
  const [localStatus, setLocalStatus] = useState(candidate.status);
  const [savingStatus, setSavingStatus] = useState(false);
  const [showAssignMenu, setShowAssignMenu] = useState(false);
  const [users, setUsers] = useState([]);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    setLocalStatus(candidate.status);
  }, [candidate.status]);

  // Clean phone number to remove spaces, dashes, and country code symbols
  const cleanPhoneNumber = (phone) => {
    if (!phone) return "";
    return phone.replace(/[^0-9]/g, '');
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file extension
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(ext)) {
      setUploadError("Only PDF, DOC, DOCX files allowed");
      return;
    }

    setUploading(true);
    setUploadError("");

    try {
      await candidateAPI.uploadResume(candidate.id, file);
      if (onResumeUpload) onResumeUpload(candidate.id);
      e.target.value = ""; // Reset input
    } catch (error) {
      setUploadError(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${candidate.name}?`)) return;
    
    try {
      await candidateAPI.delete(candidate.id);
      if (onDelete) onDelete(candidate.id);
    } catch (error) {
      alert(error.response?.data?.detail || "Delete failed");
    }
  };

  const handleResumeDownload = async () => {
    setDownloadingResume(true);
    setUploadError("");
    try {
      const response = await candidateAPI.downloadResume(candidate.id);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `CAND-${String(candidate.id).padStart(5, "0")}-resume`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setUploadError(error.response?.status === 403
        ? "You are not authorised to download this resume."
        : "Resume download failed.");
    } finally {
      setDownloadingResume(false);
    }
  };

  const handleStatusToggle = async () => {
    if (savingStatus) return;
    const newStatus = localStatus === "Active" ? "Inactive" : "Active";
    const previousStatus = localStatus;
    setLocalStatus(newStatus);
    setSavingStatus(true);
    try {
      if (onStatusChange) {
        await onStatusChange(candidate.id, newStatus);
      }
    } catch (error) {
      setLocalStatus(previousStatus);
      alert(error.response?.data?.detail || "Failed to update candidate status");
    } finally {
      setSavingStatus(false);
    }
  };

  // Load users for assignment
  useEffect(() => {
    if (showAssignMenu && currentUser && ['admin', 'bm', 'am', 'tl'].includes(currentUser.role)) {
      loadUsers();
    }
  }, [showAssignMenu]);

  const loadUsers = async () => {
    try {
      const response = await axiosInstance.get('/users/list');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleAssign = async (assigneeId) => {
    setAssigning(true);
    try {
      await axiosInstance.put(`/candidates/${candidate.id}/assign`, null, {
        params: { assignee_id: assigneeId }
      });
      setShowAssignMenu(false);
      // Refresh candidate data
      window.location.reload();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to assign candidate');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className={`bg-navy-light border rounded-lg p-5 shadow-lg hover:shadow-teal/20 hover:shadow-xl transition-all ${
      isSelected ? 'border-teal border-2 ring-2 ring-teal/30' : 'border-teal/30'
    }`}>
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-start gap-3 flex-1">
          {/* Selection Checkbox */}
          {onToggleSelect && (
            <input
              type="checkbox"
              checked={isSelected || false}
              onChange={() => onToggleSelect(candidate.id)}
              className="mt-1 w-4 h-4 text-teal border-teal/30 rounded focus:ring-teal cursor-pointer accent-teal"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-heading font-semibold text-textLight">{candidate.name}</h3>
              {/* Resume Indicator */}
              {candidate.resume_path && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal/20 text-teal border border-teal/30 text-xs rounded" title="Resume uploaded">
                  <Paperclip size={12} />
                  Resume
                </span>
              )}
              {/* Call Log Count */}
              {callLogCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gold/20 text-gold border border-gold/30 text-xs rounded" title={`${callLogCount} call log(s)`}>
                  <MessageSquare size={12} />
                  {callLogCount}
                </span>
              )}
            </div>
            
            {/* Status Badge with Toggle */}
            <div className="flex items-center gap-2 mt-2">
              <span className={`inline-flex items-center px-2 py-1 text-xs rounded font-medium border ${
                localStatus === 'Active' 
                  ? 'bg-teal/20 text-teal border-teal/30' 
                  : 'bg-textLight/10 text-textLight/60 border-textLight/20'
              }`}>
                {localStatus}
              </span>
              
              {/* Status Toggle Switch */}
              <button
                onClick={handleStatusToggle}
                disabled={savingStatus}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shadow-lg ${
                  localStatus === 'Active' ? 'bg-teal shadow-teal/30' : 'bg-textLight/30 shadow-none'
                }`}
                title="Toggle status"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-md ${
                    localStatus === 'Active' ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit && onEdit(candidate)}
            className="text-teal hover:text-teal-light p-1 transition-colors"
            title="Edit candidate"
            aria-label={`Edit ${candidate.name}`}
          >
            <Pencil size={18} />
          </button>
          <button
            onClick={handleDelete}
            className="text-gold hover:text-gold-light p-1 transition-colors"
            title="Delete candidate"
            aria-label={`Delete ${candidate.name}`}
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-textLight/80">
          <Mail size={16} className="mr-2 text-teal" />
          <span>{candidate.email}</span>
        </div>
        
        {candidate.phone && (
          <div className="flex items-center text-sm text-textLight/80">
            <Phone size={16} className="mr-2 text-teal" />
            <span>{candidate.phone}</span>
          </div>
        )}
        
        {candidate.role && (
          <div className="flex items-center text-sm text-textLight/80">
            <Briefcase size={16} className="mr-2 text-gold" />
            <span>{candidate.role}</span>
          </div>
        )}
        
        {candidate.location && (
          <div className="flex items-center text-sm text-textLight/80">
            <MapPin size={16} className="mr-2 text-gold" />
            <span>{candidate.location}</span>
          </div>
        )}
      </div>

      {/* Resume Link */}
      {candidate.resume_path && (
        <button
          type="button"
          onClick={handleResumeDownload}
          disabled={downloadingResume}
          className="flex items-center text-sm text-teal hover:text-teal-light mb-3 transition-colors"
        >
          <FileText size={16} className="mr-2" />
          {downloadingResume ? "Downloading Resume..." : "Download Resume"}
        </button>
      )}

      {/* Upload Error */}
      {uploadError && (
        <p className="text-xs text-gold mb-2">{uploadError}</p>
      )}

      {/* Assignment for managers - DISABLED IN PRODUCTION */}
      {false && currentUser && ['admin', 'bm', 'am', 'tl'].includes(currentUser.role) && (
        <div className="relative mb-2">
          <button
            onClick={() => setShowAssignMenu(!showAssignMenu)}
            className="flex items-center px-3 py-2 bg-gold/20 text-gold border border-gold/30 rounded hover:bg-gold/30 text-sm font-medium transition-all"
          >
            <UserPlus size={16} className="mr-1" />
            Assign To
          </button>
          
          {showAssignMenu && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-navy-light border border-teal/30 rounded-lg shadow-lg z-10">
              <div className="p-2 max-h-48 overflow-y-auto">
                {users.filter(u => ['recruiter', 'tl', 'am', 'bm'].includes(u.role)).map(user => (
                  <button
                    key={user.id}
                    onClick={() => handleAssign(user.id)}
                    disabled={assigning || user.id === candidate.created_by}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-teal/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {user.name} ({user.role})
                    {user.id === candidate.created_by && " (Current)"}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="space-y-2 pt-3 border-t border-teal/20">
        {/* Primary Actions */}
        <div className="flex gap-2">
          <label className="flex-1 cursor-pointer">
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleResumeUpload}
              className="hidden"
              disabled={uploading}
            />
            <div className="flex items-center justify-center px-3 py-2 bg-teal/20 text-teal border border-teal/30 rounded hover:bg-teal/30 hover:border-teal text-sm font-medium transition-all">
              <Upload size={16} className="mr-1" />
              {uploading ? "Uploading..." : "Upload Resume"}
            </div>
          </label>
        </div>

        {/* Unified Quick-Action Bar (Call • WhatsApp • Add Call Log) */}
        <div className="mt-2">
          <QuickActionBar
            candidate={candidate}
            onAddLog={(cand) => onCallLog && onCallLog(cand)}
            onAddFollowUp={onAddFollowUp ? (cand) => onAddFollowUp(cand) : null}
          />
        </div>
      </div>
    </div>
  );
}

export default CandidateCard;
